/*
 * Registrierportal, Inhaltspflege (/settings/portal, Recht portal.admin;
 * docs/registrierportal.md §3). Je Dokument ein Entwurf, der veröffentlicht
 * wird; veröffentlichte Fassungen sind unveränderlich, damit belegbar bleibt,
 * welchem Wortlaut jemand zugestimmt hat. Marke und Texte sind JSON-Fassungen
 * und werden hier als Formular gepflegt.
 */

import { Fragment } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { api, ApiError } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { showToast } from '../components/Toaster';
import { sessionSignal } from '../auth/session';
import { RichText } from '../portal/markdown';
import { TEXT_FIELDS, isColor, kindLabel, type PortalBranding, type PortalTexts } from '../portal/types';
import './PortalAdminPage.css';

const PERMISSION = 'portal.admin';

interface VersionDto {
  versionId: string;
  version: number;
  title: string;
  body: string;
  consentLabel: string | null;
  changeNote: string | null;
  contentHash: string;
  publishedAt: string | null;
  updatedAt: string;
}

interface DocumentRow {
  id: string;
  kind: string;
  language: string;
  consentMode: 'none' | 'required' | 'optional';
  sortOrder: number;
  isJson: boolean;
  current: VersionDto | null;
  draft: VersionDto | null;
  versionCount: number;
}

interface ConsentRow {
  id: string;
  kind: string;
  title: string;
  version: number;
  contentHash: string;
  givenAt: string;
  confirmedAt: string | null;
  withdrawnAt: string | null;
}

function errorText(e: unknown): string {
  if (e instanceof ApiError) return (e.body as { error?: string } | null)?.error ?? `HTTP ${e.status}`;
  return e instanceof Error ? e.message : String(e);
}

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('de-AT', { dateStyle: 'short', timeStyle: 'short' }) : '');

const CONSENT_LABELS: Record<DocumentRow['consentMode'], string> = {
  none: 'Keine Zustimmung',
  required: 'Zustimmung Pflicht',
  optional: 'Zustimmung freiwillig',
};

export function PortalAdminPage() {
  const canAdmin = (sessionSignal.value.user?.permissions ?? []).includes(PERMISSION);
  const [tick, setTick] = useState(0);
  const docs = useAsync(
    (s) => (canAdmin ? api.get<DocumentRow[]>('/api/portal-admin/documents?lang=de', s) : Promise.resolve<DocumentRow[]>([])),
    [canAdmin, tick],
  );
  const [selected, setSelected] = useState<string | null>(null);
  const reload = () => setTick((t) => t + 1);

  if (!canAdmin) {
    return <AppShell title="Registrierportal"><p class="hv-portaladmin__warn">Dafür braucht es die Berechtigung „portal.admin“.</p></AppShell>;
  }

  const list = docs.status === 'ok' ? docs.data : [];
  const current = list.find((d) => d.id === selected) ?? null;

  return (
    <AppShell title="Registrierportal">
      <div class="hv-portaladmin">
        <p class="hv-portaladmin__hint">
          Texte, Marke und Rechtstexte des Registrierportals dieser Instanz. Änderungen entstehen als Entwurf und gelten
          erst nach dem Veröffentlichen. Eine veröffentlichte Fassung bleibt unverändert gespeichert, samt Prüfsumme;
          jede Zustimmung verweist auf genau diese Fassung. Das Portal nimmt Registrierungen erst an, wenn
          Datenschutzerklärung und Impressum veröffentlicht sind.
        </p>
        <div class="hv-portaladmin__layout">
          <nav class="hv-portaladmin__list">
            {docs.status === 'loading' && <p class="muted">Lade…</p>}
            {docs.status === 'error' && <p class="hv-portaladmin__warn">{errorText(docs.error)}</p>}
            {list.map((d) => (
              <button type="button" key={d.id} class={`hv-portaladmin__item${d.id === selected ? ' is-active' : ''}`} onClick={() => setSelected(d.id)}>
                <b>{kindLabel(d.kind)}</b>
                <span>
                  {d.current ? `Fassung ${d.current.version}` : 'Noch nicht veröffentlicht'}
                  {d.draft ? ', Entwurf offen' : ''}
                </span>
              </button>
            ))}
            <NewDocument onCreated={reload} />
          </nav>
          <main class="hv-portaladmin__main">
            {!current && <p class="muted">Links ein Dokument wählen.</p>}
            {current && <DocumentEditor key={`${current.id}-${tick}`} doc={current} onChanged={reload} />}
          </main>
        </div>
        <ConsentLookup />
      </div>
    </AppShell>
  );
}

function NewDocument({ onCreated }: { onCreated: () => void }) {
  const [kind, setKind] = useState('');
  const [mode, setMode] = useState<'required' | 'optional'>('optional');
  const create = async () => {
    try {
      await api.post('/api/portal-admin/documents', { kind, language: 'de', consentMode: mode });
      setKind('');
      onCreated();
    } catch (e) {
      showToast({ tone: 'error', title: 'Nicht angelegt', message: errorText(e) });
    }
  };
  return (
    <details class="hv-portaladmin__new">
      <summary>Weitere Einwilligung anlegen</summary>
      <input placeholder="Kennung, z. B. consent.newsletter" value={kind} onInput={(e) => setKind((e.target as HTMLInputElement).value)} />
      <select value={mode} onChange={(e) => setMode((e.target as HTMLSelectElement).value as 'required' | 'optional')}>
        <option value="optional">Freiwillig</option>
        <option value="required">Pflicht</option>
      </select>
      <button type="button" class="hv-btn hv-btn--sm" disabled={kind.trim().length < 2} onClick={() => void create()}>Anlegen</button>
    </details>
  );
}

function DocumentEditor({ doc, onChanged }: { doc: DocumentRow; onChanged: () => void }) {
  const base = doc.draft ?? doc.current;
  const [title, setTitle] = useState(base?.title ?? kindLabel(doc.kind));
  const [body, setBody] = useState(base?.body ?? '');
  const [consentLabel, setConsentLabel] = useState(base?.consentLabel ?? '');
  const [changeNote, setChangeNote] = useState(doc.draft?.changeNote ?? '');
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const saveDraft = async (): Promise<boolean> => {
    setBusy(true);
    try {
      await api.put(`/api/portal-admin/documents/${doc.id}/draft`, { title, body, consentLabel, changeNote });
      showToast({ tone: 'success', title: 'Entwurf gespeichert' });
      onChanged();
      return true;
    } catch (e) {
      showToast({ tone: 'error', title: 'Nicht gespeichert', message: errorText(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!window.confirm('Diese Fassung veröffentlichen? Sie gilt ab sofort und lässt sich danach nicht mehr ändern.')) return;
    if (!(await saveDraft())) return;
    setBusy(true);
    try {
      await api.post(`/api/portal-admin/documents/${doc.id}/publish`, {});
      showToast({ tone: 'success', title: 'Veröffentlicht' });
      onChanged();
    } catch (e) {
      showToast({ tone: 'error', title: 'Nicht veröffentlicht', message: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (!window.confirm('Entwurf verwerfen?')) return;
    try {
      await api.delete(`/api/portal-admin/documents/${doc.id}/draft`);
      onChanged();
    } catch (e) {
      showToast({ tone: 'error', title: 'Nicht verworfen', message: errorText(e) });
    }
  };

  const setMode = async (consentMode: string) => {
    try {
      await api.put(`/api/portal-admin/documents/${doc.id}`, { consentMode });
      onChanged();
    } catch (e) {
      showToast({ tone: 'error', title: 'Nicht gespeichert', message: errorText(e) });
    }
  };

  return (
    <div class="hv-portaladmin__editor">
      <header class="hv-portaladmin__head">
        <h2>{kindLabel(doc.kind)}</h2>
        {doc.current
          ? <span class="muted">Gültig: Fassung {doc.current.version} vom {when(doc.current.publishedAt)}, Prüfsumme {doc.current.contentHash.slice(0, 12)}</span>
          : <span class="hv-portaladmin__warn">Noch keine Fassung veröffentlicht</span>}
        {!doc.isJson && (
          <select value={doc.consentMode} onChange={(e) => void setMode((e.target as HTMLSelectElement).value)}>
            {Object.entries(CONSENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        )}
      </header>

      <label class="hv-portaladmin__field">Titel
        <input value={title} maxLength={200} onInput={(e) => setTitle((e.target as HTMLInputElement).value)} />
      </label>

      {doc.kind === 'branding' && <BrandingForm value={body} onChange={setBody} />}
      {doc.kind === 'texts' && <TextsForm value={body} onChange={setBody} />}
      {!doc.isJson && (
        <>
          {doc.consentMode !== 'none' && (
            <label class="hv-portaladmin__field">Zustimmungstext (neben dem Kästchen im Formular)
              <input value={consentLabel} maxLength={1000} placeholder="Ich habe die Datenschutzerklärung gelesen."
                     onInput={(e) => setConsentLabel((e.target as HTMLInputElement).value)} />
            </label>
          )}
          <div class="hv-portaladmin__split">
            <label class="hv-portaladmin__field">Inhalt (# Überschrift, ## Unterüberschrift, - Liste, **fett**)
              <textarea rows={22} value={body} onInput={(e) => setBody((e.target as HTMLTextAreaElement).value)} />
            </label>
            <div class="hv-portaladmin__preview"><RichText source={body} /></div>
          </div>
        </>
      )}

      <label class="hv-portaladmin__field">Änderungsvermerk (intern)
        <input value={changeNote} maxLength={500} onInput={(e) => setChangeNote((e.target as HTMLInputElement).value)} />
      </label>

      <div class="hv-portaladmin__buttons">
        <button type="button" class="hv-btn" disabled={busy} onClick={() => void saveDraft()}>Entwurf speichern</button>
        <button type="button" class="hv-btn hv-btn--primary" disabled={busy} onClick={() => void publish()}>Veröffentlichen</button>
        {doc.draft && <button type="button" class="hv-btn" disabled={busy} onClick={() => void discard()}>Entwurf verwerfen</button>}
        <button type="button" class="hv-btn" onClick={() => setShowHistory((s) => !s)}>
          {showHistory ? 'Verlauf ausblenden' : `Verlauf (${doc.versionCount})`}
        </button>
      </div>
      {showHistory && <History documentId={doc.id} />}
    </div>
  );
}

function parseJson<T>(value: string): T {
  try { return (value ? JSON.parse(value) : {}) as T; } catch { return {} as T; }
}

function BrandingForm({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const b = parseJson<PortalBranding>(value);
  const set = (patch: Partial<PortalBranding>) => onChange(JSON.stringify({ ...b, ...patch }, null, 2));
  const upload = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await api.postForm<{ id: string }>('/api/portal-admin/assets', form);
      set({ logoAssetId: r.id });
    } catch (e) {
      showToast({ tone: 'error', title: 'Logo nicht hochgeladen', message: errorText(e) });
    }
  };
  const color = (key: 'primaryColor' | 'primaryTextColor' | 'backgroundColor' | 'textColor', label: string, fallback: string) => (
    <label class="hv-portaladmin__field hv-portaladmin__color">{label}
      <span>
        <input type="color" value={isColor(b[key]) ? b[key]! : fallback} onInput={(e) => set({ [key]: (e.target as HTMLInputElement).value })} />
        <input value={b[key] ?? ''} placeholder="Vorgabe" onInput={(e) => set({ [key]: (e.target as HTMLInputElement).value || null })} />
      </span>
    </label>
  );
  return (
    <div class="hv-portaladmin__form">
      <label class="hv-portaladmin__field">Produktname
        <input value={b.productName ?? ''} placeholder="Vorgabe aus der Marke der Instanz" onInput={(e) => set({ productName: (e.target as HTMLInputElement).value || null })} />
      </label>
      <label class="hv-portaladmin__field">Absendername der Mails
        <input value={b.senderName ?? ''} placeholder="wie Produktname" onInput={(e) => set({ senderName: (e.target as HTMLInputElement).value || null })} />
      </label>
      <div class="hv-portaladmin__field">Logo
        <span class="hv-portaladmin__logo">
          {b.logoAssetId ? <img src={`/api/portal/assets/${b.logoAssetId}`} alt="" /> : <span class="muted">Logo aus der Marke der Instanz</span>}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                 onChange={(e) => { const f = (e.target as HTMLInputElement).files?.[0]; if (f) void upload(f); }} />
          {b.logoAssetId && <button type="button" class="hv-btn hv-btn--sm" onClick={() => set({ logoAssetId: null })}>Entfernen</button>}
        </span>
      </div>
      <label class="hv-portaladmin__field">Alternativtext des Logos
        <input value={b.logoAlt ?? ''} onInput={(e) => set({ logoAlt: (e.target as HTMLInputElement).value || null })} />
      </label>
      {color('primaryColor', 'Hauptfarbe (Knöpfe)', '#c62127')}
      {color('primaryTextColor', 'Schrift auf der Hauptfarbe', '#ffffff')}
      {color('backgroundColor', 'Hintergrund', '#f4f5f7')}
      {color('textColor', 'Schrift', '#16191d')}
    </div>
  );
}

function TextsForm({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const t = parseJson<PortalTexts>(value);
  const set = (key: keyof PortalTexts, v: string) => {
    const next: PortalTexts = { ...t, [key]: v };
    if (!v) delete next[key];
    onChange(JSON.stringify(next, null, 2));
  };
  return (
    <div class="hv-portaladmin__form">
      <p class="muted">Leere Felder verwenden die eingebaute Vorgabe.</p>
      {TEXT_FIELDS.map((f) => (
        <label key={f.key} class="hv-portaladmin__field">{f.label}
          {f.multiline
            ? <textarea rows={f.key.endsWith('Body') ? 7 : 3} value={t[f.key] ?? ''} onInput={(e) => set(f.key, (e.target as HTMLTextAreaElement).value)} />
            : <input value={t[f.key] ?? ''} onInput={(e) => set(f.key, (e.target as HTMLInputElement).value)} />}
        </label>
      ))}
    </div>
  );
}

function History({ documentId }: { documentId: string }) {
  const versions = useAsync(
    (s) => api.get<Array<{ version: VersionDto; body: string; consents: number }>>(`/api/portal-admin/documents/${documentId}/versions`, s),
    [documentId],
  );
  const [open, setOpen] = useState<string | null>(null);
  if (versions.status === 'loading') return <p class="muted">Lade…</p>;
  if (versions.status === 'error') return <p class="hv-portaladmin__warn">{errorText(versions.error)}</p>;
  return (
    <table class="hv-portaladmin__table">
      <thead><tr><th>Fassung</th><th>Veröffentlicht</th><th>Prüfsumme</th><th>Zustimmungen</th><th>Vermerk</th><th /></tr></thead>
      <tbody>
        {versions.data.map(({ version: v, body, consents }) => (
          <Fragment key={v.versionId}>
            <tr>
              <td>{v.version}{v.publishedAt ? '' : ' (Entwurf)'}</td>
              <td>{when(v.publishedAt)}</td>
              <td><code title={v.contentHash}>{v.contentHash.slice(0, 16)}</code></td>
              <td>{consents}</td>
              <td>{v.changeNote ?? ''}</td>
              <td><button type="button" class="hv-btn hv-btn--sm" onClick={() => setOpen(open === v.versionId ? null : v.versionId)}>
                {open === v.versionId ? 'Zu' : 'Ansehen'}</button></td>
            </tr>
            {open === v.versionId && (
              <tr key={`${v.versionId}-body`}><td colSpan={6}><pre class="hv-portaladmin__pre">{v.consentLabel ? `Zustimmungstext: ${v.consentLabel}\n\n` : ''}{body}</pre></td></tr>
            )}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

function ConsentLookup() {
  const [email, setEmail] = useState('');
  const [rows, setRows] = useState<ConsentRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setRows(null); setError(null); }, [email]);
  const search = async () => {
    try {
      setRows(await api.get<ConsentRow[]>(`/api/portal-admin/consents?email=${encodeURIComponent(email.trim())}`));
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <section class="hv-portaladmin__lookup">
      <h3>Nachweis einer Zustimmung</h3>
      <div class="hv-portaladmin__buttons">
        <input type="email" placeholder="E-Mail-Adresse" value={email} onInput={(e) => setEmail((e.target as HTMLInputElement).value)} />
        <button type="button" class="hv-btn" disabled={!email.includes('@')} onClick={() => void search()}>Suchen</button>
      </div>
      {error && <p class="hv-portaladmin__warn">{error}</p>}
      {rows && rows.length === 0 && <p class="muted">Keine Zustimmungen zu dieser Adresse.</p>}
      {rows && rows.length > 0 && (
        <table class="hv-portaladmin__table">
          <thead><tr><th>Dokument</th><th>Fassung</th><th>Prüfsumme</th><th>Erteilt</th><th>Bestätigt</th><th>Widerrufen</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.title}</td><td>{r.version}</td><td><code title={r.contentHash}>{r.contentHash.slice(0, 16)}</code></td>
                <td>{when(r.givenAt)}</td><td>{when(r.confirmedAt)}</td><td>{when(r.withdrawnAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
